import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GET_TRENDING_FOLLOWING } from '../../graphql/resolvers/trendingQueries/getTrendingFollowing.gql';
import { GET_TRENDING_GLOBAL } from '../../graphql/resolvers/trendingQueries/getTrendingGlobal.gql';
import type { TrendingItemInterface } from '../../interfaces/trending.interface';
import './trending-section.scss';

interface TrendingSectionProps {
  position: 'top' | 'bottom';
  onMove: () => void;
}

export default function TrendingSection({
  position,
  onMove,
}: TrendingSectionProps) {
  const [activeTab, setActiveTab] = useState<'following' | 'global'>(
    'following',
  );
  const navigate = useNavigate();

  const { data: followingData, loading: followingLoading } = useQuery(
    GET_TRENDING_FOLLOWING,
    {
      variables: { limit: 10 },
      pollInterval: 300_000,
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-only',
    },
  );

  const { data: globalData, loading: globalLoading } = useQuery(
    GET_TRENDING_GLOBAL,
    {
      variables: { limit: 10 },
      pollInterval: 600_000,
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-only',
    },
  );

  const items: TrendingItemInterface[] =
    activeTab === 'following'
      ? followingData?.trendingStories || []
      : globalData?.trendingStories || [];
  const loading = activeTab === 'following' ? followingLoading : globalLoading;

  function handleTopicClick(topic: string) {
    navigate(
      `/user/${encodeURIComponent(topic.toLowerCase())}/feed?topic=${encodeURIComponent(topic)}`,
    );
  }

  const moveLabel = position === 'top' ? 'Move down \u2193' : 'Move up \u2191';

  return (
    <div className="Trending__Section">
      <div className="Trending__Section__Header">
        <h3 className="Trending__Section__Title">Trending</h3>
        <button
          type="button"
          className="Trending__Section__MoveBtn"
          onClick={onMove}
        >
          {moveLabel}
        </button>
      </div>

      <div className="Trending__Tabs">
        <button
          type="button"
          className={`Trending__Tabs__Tab ${activeTab === 'following' ? 'Trending__Tabs__Tab--active' : ''}`}
          onClick={() => setActiveTab('following')}
        >
          Following
        </button>
        <button
          type="button"
          className={`Trending__Tabs__Tab ${activeTab === 'global' ? 'Trending__Tabs__Tab--active' : ''}`}
          onClick={() => setActiveTab('global')}
        >
          Global
        </button>
      </div>

      {loading ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <div className="Trending__Empty">
          {activeTab === 'following'
            ? 'Nothing trending in your topics yet'
            : 'No trending stories right now'}
        </div>
      ) : (
        <div>
          {items.map((item) => (
            <div
              key={item.topic}
              className="Trending__List__Item"
              onClick={() => handleTopicClick(item.topic)}
            >
              <div className="Trending__List__Topic">{item.topic}</div>
              <div className="Trending__List__Label">{item.label}</div>
              <div className="Trending__List__Count">
                {item.storyCount} {item.storyCount === 1 ? 'story' : 'stories'}
              </div>
            </div>
          ))}
          <button
            type="button"
            className="Trending__SeeAll"
            onClick={() => navigate(`/search`)}
          >
            See all trending &rarr;
          </button>
        </div>
      )}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="Trending__Skeleton__Row">
          <div className="Trending__Skeleton__Line Trending__Skeleton__Line--short" />
          <div className="Trending__Skeleton__Line Trending__Skeleton__Line--medium" />
          <div className="Trending__Skeleton__Line Trending__Skeleton__Line--long" />
        </div>
      ))}
    </div>
  );
}
